import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconSearch, IconShoppingCart, IconX, IconChevronRight, IconChevronLeft, IconArrowLeft } from '@tabler/icons-react';
import Confetti from 'react-confetti';
import axiosInstance from '../api/axiosConfig';

const MenuItem = ({ name, price, description, image, addToCart }) => (
    <motion.div
        className="bg-white p-4 rounded-lg shadow-md flex flex-col items-center"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
    >
        <img src={image} alt={name} className="w-32 h-32 object-cover rounded-md mb-2" />
        <h3 className="font-bold text-lg">{name}</h3>
        <p className="text-sm text-gray-600 text-center mb-2">{description}</p>
        <p className="text-orange-500 font-semibold">RM {price.toFixed(2)}</p>
        <button
            onClick={() => addToCart({ name, price, description, image })}
            className="mt-2 bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition-colors"
        >
            Add to Cart
        </button>
    </motion.div>
);

const Cart = ({ items, toggleCart, removeFromCart, proceedToCheckout }) => (
    <motion.div
        className="fixed top-4 right-4 bg-white p-4 rounded-lg shadow-lg z-50 border-2 border-orange-500"
        initial={{ opacity: 0, scale: 0.8, x: 100 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        exit={{ opacity: 0, scale: 0.8, x: 100 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    >
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-orange-500">My Cart</h3>
            <button onClick={toggleCart} className="text-orange-500 hover:text-orange-700">
                <IconX size={24} />
            </button>
        </div>
        {items.length === 0 ? (
            <p>Your cart is empty</p>
        ) : (
            <>
                {items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center mb-2">
                        <span>{item.name}</span>
                        <div className="flex items-center">
                            <span className="mr-2">RM {item.price.toFixed(2)}</span>
                            <button
                                onClick={() => removeFromCart(index)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <IconX size={18} />
                            </button>
                        </div>
                    </div>
                ))}
                <div className="mt-4 font-bold text-orange-500">
                    Total: RM {items.reduce((sum, item) => sum + item.price, 0).toFixed(2)}
                </div>
                <button
                    className="mt-4 bg-orange-500 text-white w-full py-2 rounded-md hover:bg-orange-600"
                    onClick={proceedToCheckout}
                >
                    Checkout
                </button>
            </>
        )}
    </motion.div>
);

const CheckoutScreen = ({ items, total, handlePurchase, verificationNumber, onBack }) => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-8">
        {verificationNumber ? (
            <motion.div
                className="w-full max-w-md bg-white rounded-lg shadow-md p-6 text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                <h2 className="text-3xl font-bold text-green-500 mb-4">Order Successful!</h2>
                <p className="text-lg">Your verification number is:</p>
                <p className="text-4xl font-bold text-orange-500 mt-4">{verificationNumber}</p>
                <p className="text-lg mt-6">Please provide this number to the lunch staff to verify your order.</p>
            </motion.div>
        ) : (
            <motion.div
                className="w-full max-w-3xl bg-white rounded-lg shadow-md p-6"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex items-center mb-6">
                    <button onClick={onBack} className="text-orange-500 hover:text-orange-700 mr-4">
                        <IconArrowLeft size={28} />
                    </button>
                    <h2 className="text-3xl font-bold text-orange-500">Checkout</h2>
                </div>
                <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4">Order Summary</h3>
                    <div>
                        {items.map((item, index) => (
                            <div key={index} className="flex items-center mb-4 border-b pb-4">
                                <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-md mr-4" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-lg">{item.name}</h4>
                                    <p className="text-sm text-gray-600">{item.description}</p>
                                </div>
                                <div className="text-orange-500 font-semibold text-lg">
                                    RM {item.price.toFixed(2)}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between items-center mt-6">
                        <span className="text-2xl font-bold">Total:</span>
                        <span className="text-3xl font-bold text-orange-500">RM {total.toFixed(2)}</span>
                    </div>
                </div>
                <button
                    className="w-full bg-green-500 text-white py-4 rounded-md text-xl font-semibold hover:bg-green-600 transition-colors"
                    onClick={handlePurchase}
                >
                    Place Order
                </button>
            </motion.div>
        )}
    </div>
);

const CustomOrderBuilder = ({ addToCart }) => {
    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(1);
    const [order, setOrder] = useState({
        base: '',
        dressing: '',
        cheese: '',
        meat: '',
        toppings: []
    });
    const [showConfetti, setShowConfetti] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);

    const steps = [
        {
            name: 'Base',
            options: [
                { name: 'Wrap', image: 'https://via.placeholder.com/100?text=Wrap' },
                { name: 'Panini', image: 'https://via.placeholder.com/100?text=Panini' },
                { name: 'Texas Toast', image: 'https://via.placeholder.com/100?text=Texas+Toast' }
            ]
        },
        {
            name: 'Dressing',
            options: [
                { name: 'Mayo', image: 'https://via.placeholder.com/100?text=Mayo' },
                { name: 'Ranch', image: 'https://via.placeholder.com/100?text=Ranch' },
                { name: 'Italian', image: 'https://via.placeholder.com/100?text=Italian' },
                { name: 'BBQ Sauce', image: 'https://via.placeholder.com/100?text=BBQ+Sauce' }
            ]
        },
        {
            name: 'Cheese',
            options: [
                { name: 'Cheddar', image: 'https://via.placeholder.com/100?text=Cheddar' },
                { name: 'Swiss', image: 'https://via.placeholder.com/100?text=Swiss' },
                { name: 'Provolone', image: 'https://via.placeholder.com/100?text=Provolone' },
                { name: 'American', image: 'https://via.placeholder.com/100?text=American' }
            ]
        },
        {
            name: 'Meat',
            options: [
                { name: 'Turkey', image: 'https://via.placeholder.com/100?text=Turkey' },
                { name: 'Ham', image: 'https://via.placeholder.com/100?text=Ham' },
                { name: 'Chicken', image: 'https://via.placeholder.com/100?text=Chicken' },
                { name: 'Roast Beef', image: 'https://via.placeholder.com/100?text=Roast+Beef' }
            ]
        },
        {
            name: 'Toppings',
            options: [
                { name: 'Lettuce', image: 'https://via.placeholder.com/100?text=Lettuce' },
                { name: 'Tomato', image: 'https://via.placeholder.com/100?text=Tomato' },
                { name: 'Onion', image: 'https://via.placeholder.com/100?text=Onion' },
                { name: 'Pickles', image: 'https://via.placeholder.com/100?text=Pickles' },
                { name: 'Avocado', image: 'https://via.placeholder.com/100?text=Avocado' }
            ],
            multiple: true
        }
    ];

    const handleSelect = (option) => {
        if (steps[step].multiple) {
            setOrder((prev) => ({
                ...prev,
                [steps[step].name.toLowerCase()]: prev[steps[step].name.toLowerCase()].includes(option.name)
                    ? prev[steps[step].name.toLowerCase()].filter((item) => item !== option.name)
                    : [...prev[steps[step].name.toLowerCase()], option.name]
            }));
        } else {
            setOrder((prev) => ({ ...prev, [steps[step].name.toLowerCase()]: option.name }));
        }
    };

    const nextStep = () => {
        if (step < steps.length - 1) {
            setDirection(1);
            setStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        if (step > 0) {
            setDirection(-1);
            setStep((prev) => prev - 1);
        }
    };

    const handleComplete = () => {
        const customItem = {
            name: `Custom ${order.base}`,
            price: 5.0,
            description: `${order.base} with ${order.meat}, ${order.cheese}, ${order.dressing}, and ${order.toppings.join(', ')}`,
            image: 'https://via.placeholder.com/100?text=Custom+Order'
        };
        addToCart(customItem);
        setShowConfetti(true);
        setShowCheckout(true);
        setTimeout(() => {
            setShowConfetti(false);
        }, 3000);
        setOrder({
            base: '',
            dressing: '',
            cheese: '',
            meat: '',
            toppings: []
        });
        setStep(0);
    };

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 300 : -300,
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            x: direction > 0 ? -300 : 300,
            opacity: 0
        })
    };

    return (
        <div className="flex flex-col items-center relative">
            {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}
            <h2 className="text-2xl font-bold mb-4">Build Your Custom Order</h2>
            {!showCheckout ? (
                <div className="w-full max-w-lg">
                    <AnimatePresence initial={false} custom={direction} mode="wait">
                        <motion.div
                            key={step}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: 'spring', stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 }
                            }}
                            className="bg-white p-6 rounded-lg shadow-md"
                            style={{ minHeight: '400px' }}
                        >
                            <h3 className="text-xl font-semibold mb-4">{steps[step].name}</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {steps[step].options.map((option) => (
                                    <motion.button
                                        key={option.name}
                                        className={`p-4 rounded-lg border-2 flex flex-col items-center ${
                                            steps[step].multiple
                                                ? order[steps[step].name.toLowerCase()].includes(option.name)
                                                    ? 'bg-orange-500 text-white border-orange-500'
                                                    : 'bg-white text-gray-700 border-gray-300'
                                                : order[steps[step].name.toLowerCase()] === option.name
                                                ? 'bg-orange-500 text-white border-orange-500'
                                                : 'bg-white text-gray-700 border-gray-300'
                                        }`}
                                        onClick={() => handleSelect(option)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <img src={option.image} alt={option.name} className="w-16 h-16 object-cover mb-2 rounded-md" />
                                        <span>{option.name}</span>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                    <div className="flex justify-between mt-6">
                        <button
                            onClick={prevStep}
                            disabled={step === 0}
                            className={`flex items-center px-4 py-2 rounded-md ${
                                step === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-orange-500 text-white hover:bg-orange-600'
                            }`}
                        >
                            <IconChevronLeft size={20} />
                            Back
                        </button>
                        {step < steps.length - 1 ? (
                            <button
                                onClick={nextStep}
                                disabled={
                                    !order[steps[step].name.toLowerCase()] ||
                                    order[steps[step].name.toLowerCase()].length === 0
                                }
                                className={`flex items-center px-4 py-2 rounded-md ${
                                    !order[steps[step].name.toLowerCase()] ||
                                    order[steps[step].name.toLowerCase()].length === 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-orange-500 text-white hover:bg-orange-600'
                                }`}
                            >
                                Next
                                <IconChevronRight size={20} />
                            </button>
                        ) : (
                            <button
                                onClick={handleComplete}
                                disabled={order.toppings.length === 0}
                                className={`px-4 py-2 rounded-md ${
                                    order.toppings.length === 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-orange-500 text-white hover:bg-orange-600'
                                }`}
                            >
                                Add to Cart
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                    <h2 className="text-3xl font-bold text-green-500">Added to Cart</h2>
                    <p className="text-lg text-gray-700 mt-4">Your custom order has been added to the cart.</p>
                    <p className="text-lg text-gray-700 mt-2">Please proceed to checkout.</p>
                </div>
            )}
        </div>
    );
};

const Dashboard = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentCategory, setCurrentCategory] = useState('All Items');
    const [cartItems, setCartItems] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCheckout, setIsCheckout] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [verificationNumber, setVerificationNumber] = useState('');

    const addToCart = (item) => {
        setCartItems([...cartItems, item]);
        setIsCartOpen(true);
    };

    const removeFromCart = (index) => {
        const newCartItems = cartItems.filter((_, i) => i !== index);
        setCartItems(newCartItems);
    };

    const toggleCart = () => {
        setIsCartOpen(!isCartOpen);
    };

    const proceedToCheckout = () => {
        setIsCheckout(true);
        setIsCartOpen(false);
    };

    const handleBackToCart = () => {
        setIsCheckout(false);
    };

    const handlePurchase = async () => {
        try {
            const response = await axiosInstance.post('/api/orders', {
                items: cartItems,
                total: totalAmount
            });

            if (response.data) {
                setVerificationNumber(response.data.verificationNumber);
                setShowConfetti(true);
                setTimeout(() => {
                    setShowConfetti(false);
                    setCartItems([]);
                }, 3000);
            }
        } catch (error) {
            console.error('Error placing order:', error);
            alert(error.response?.data?.message || 'An error occurred while placing your order.');
        }
    };

    const totalAmount = cartItems.reduce((sum, item) => sum + item.price, 0);

    const menuItems = [
        { name: 'Turkey & Avocado Wrap', price: 4.15, description: 'Turkey, lettuce, tomatoes, avocado, and mayo', image: '/images/turkey-avocado-wrap.png', category: 'Deli Wraps' },
        { name: 'Ham & Swiss Wrap', price: 4.15, description: 'Ham, lettuce, tomatoes, Swiss cheese, and mustard', image: '/images/ham-swiss-wrap.png', category: 'Deli Wraps' },
        { name: 'BBQ Chicken Wrap', price: 4.15, description: 'Breaded chicken, BBQ sauce, lettuce, pickles', image: '/images/bbq-chicken-wrap.png', category: 'Chicken Wraps' },
        { name: 'Spicy Chicken & Black Bean Wrap', price: 4.15, description: 'Chicken, black beans, jalapeños, lettuce, and ranch', image: '/images/spicy-chicken-black-bean-wrap.png', category: 'Chicken Wraps' },
        { name: 'Mediterranean Veggie Wrap', price: 4.15, description: 'Garbanzo beans, lettuce, tomatoes, onions, olives, and Italian dressing', image: '/images/mediterranean-veggie-wrap.png', category: 'Vegetarian Wraps' },
        { name: 'Roast Beef & Pickle Wrap', price: 4.15, description: 'Roast beef, pickles, lettuce, tomatoes, and mayo', image: '/images/roast-beef-pickle-wrap.png', category: 'Deli Wraps' },
        { name: 'Classic Turkey Panini', price: 4.15, description: 'Turkey, lettuce, tomatoes, and mayo on toasted bread', image: '/images/classic-turkey-panini.png', category: 'Deli Paninis' },
        { name: 'Ham & Cheese Panini', price: 4.15, description: 'Ham, Swiss cheese, tomatoes, and mustard on toasted bread', image: '/images/ham-cheese-panini.png', category: 'Deli Paninis' },
        { name: 'BBQ Chicken Panini', price: 4.15, description: 'Breaded chicken, BBQ sauce, pickles, and cheese on toasted bread', image: '/images/bbq-chicken-panini.png', category: 'Chicken Paninis' },
        { name: 'Spicy Chicken & Black Bean Panini', price: 4.15, description: 'Chicken, black beans, jalapeños, and ranch on toasted bread', image: '/images/spicy-chicken-black-bean-panini.png', category: 'Chicken Paninis' },
        { name: 'Mediterranean Veggie Panini', price: 4.15, description: 'Garbanzo beans, spinach, tomatoes, onions, olives, and Italian dressing on toasted bread', image: '/images/mediterranean-veggie-panini.png', category: 'Vegetarian Paninis' },
        { name: 'Roast Beef & Avocado Panini', price: 4.15, description: 'Roast beef, avocado, lettuce, and mayo on toasted bread', image: '/images/roast-beef-avocado-panini.png', category: 'Deli Paninis' },
        { name: 'French Fries', price: 4.0, description: 'Crispy golden fries', image: '/images/french-fries.png', category: 'Sides' },
        { name: 'Onion Rings', price: 5.0, description: 'Crispy battered onion rings', image: '/images/onion-rings.png', category: 'Sides' },
        { name: 'Iced Tea', price: 2.5, description: 'Refreshing iced tea', image: 'icytea-2.png', category: 'Drinks' },
        { name: 'Chocolate Milk', price: 4.0, description: 'A rich, creamy blend of smooth chocolate and fresh milk', image: 'chocolatemilk.png', category: 'Drinks' },
        { name: 'Milk', price: 4.0, description: 'Rich, creamy, fresh milk', image: 'milk.png', category: 'Drinks' },
        { name: 'Water', price: 2.0, description: 'Pure, refreshing water', image: 'waterbottle.png', category: 'Drinks' },
        { name: 'Capri Sun', price: 2.5, description: 'Fruity, refreshing juice drink', image: 'caprisun.png', category: 'Drinks' },
        { name: 'Snapple', price: 2.5, description: 'Flavorful beverage made from natural ingredients', image: 'snapple.png', category: 'Drinks' }
    ];

    const categories = [
        'All Items',
        'Deli Wraps',
        'Deli Paninis',
        'Chicken Wraps',
        'Vegetarian Wraps',
        'Vegetarian Paninis',
        'Sides',
        'Drinks',
        'Custom Order'
    ];

    const filteredItems = menuItems.filter(
        (item) =>
            (currentCategory === 'All Items' || item.category === currentCategory) &&
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="relative">
            {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}
            {!isCheckout ? (
                <div className="flex min-h-screen bg-gray-100 relative">
                    <div className="w-64 bg-white shadow-md">
                        <div className="p-4">
                            <h2 className="text-xl font-bold mb-4 text-orange-500">Categories</h2>
                            <ul>
                                {categories.map((category, index) => (
                                    <li key={index} className="mb-2">
                                        <button
                                            onClick={() => setCurrentCategory(category)}
                                            className={`w-full text-left py-2 px-4 rounded transition-colors ${
                                                currentCategory === category
                                                    ? 'bg-orange-500 text-white'
                                                    : 'text-gray-600 hover:bg-orange-100'
                                            }`}
                                        >
                                            {category}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="flex-1 p-8">
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-bold text-orange-500">WrappedXpress Menu</h1>
                            <motion.button
                                className="bg-orange-500 text-white p-2 rounded-full relative"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={toggleCart}
                            >
                                <IconShoppingCart size={24} />
                                {cartItems.length > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                        {cartItems.length}
                                    </span>
                                )}
                            </motion.button>
                        </div>
                        <div className="mb-6 relative">
                            <input
                                type="text"
                                placeholder="Search our menu..."
                                className="w-full p-2 pl-10 rounded-md border border-orange-300 focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <IconSearch className="absolute left-3 top-3 text-orange-400" />
                        </div>
                        {currentCategory === 'Custom Order' ? (
                            <CustomOrderBuilder addToCart={addToCart} />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {filteredItems.map((item, index) => (
                                    <MenuItem key={index} {...item} addToCart={addToCart} />
                                ))}
                            </div>
                        )}
                    </div>
                    <AnimatePresence>
                        {isCartOpen && (
                            <Cart
                                items={cartItems}
                                toggleCart={toggleCart}
                                removeFromCart={removeFromCart}
                                proceedToCheckout={proceedToCheckout}
                            />
                        )}
                    </AnimatePresence>
                </div>
            ) : (
                <CheckoutScreen
                    items={cartItems}
                    total={totalAmount}
                    handlePurchase={handlePurchase}
                    verificationNumber={verificationNumber}
                    onBack={handleBackToCart}
                />
            )}
        </div>
    );
};

export default Dashboard;
